'use client';

import { useEffect, useState, useMemo, forwardRef, useImperativeHandle, useCallback } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import TransferListTable from '../../../../components/TransferListTable';
import { createVehicleForRole, updateVehicleForRole } from '../../../../stores/roleStore';
import { useLanguage } from '../../../../utils/i18n';
const normalize = (row) => {
  return {
    id: String(row.id),
    license_plate: row.license_plate ?? '-',
    vehicle_type: row.join?.vehicle_type_name ?? row.vehicle_type ?? '-',
    name: row.name ?? '-',
    company: row.join?.company_name ?? row.company ?? 'Unknown Company',
    _raw: row
  };
};

const VehicleForRoleDialog = forwardRef(function VehicleForRoleDialog(
  { roleName, dataVehicleList, dataSearchVehicle, onDirtyChange, onSaved },
  ref
) {
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const { t } = useLanguage();

  const roleVehicleObj = useMemo(() => {
    if (Array.isArray(dataVehicleList) && dataVehicleList.length > 0) {
      return dataVehicleList[0];
    }
    return dataVehicleList || null;
  }, [dataVehicleList]);

  useEffect(() => {
    setLoading(true);
    try {
      const assignedIds = new Set((roleVehicleObj?.vehicle_id || roleVehicleObj?.vehicle || []).map((id) => String(id)));
      const allVehicles = (dataSearchVehicle || []).map(normalize);

      const assigned = [];
      const available = [];

      allVehicles.forEach((v) => {
        if (assignedIds.has(v.id)) {
          assigned.push(v);
        } else {
          available.push(v);
        }
      });

      const sortByCompany = (a, b) => a.company.localeCompare(b.company);
      available.sort(sortByCompany);
      assigned.sort(sortByCompany);

      setLeftItems(available);
      setRightItems(assigned);

      setDirty(false);
      if (typeof onDirtyChange === 'function') onDirtyChange(false);
    } catch (err) {
      console.error('Error mapping vehicles:', err);
    } finally {
      setLoading(false);
    }
  }, [roleVehicleObj, dataSearchVehicle, onDirtyChange]);

  const columns = useMemo(
    () => [
      { field: 'license_plate', label: t('general.licensePlate', 'License Plate') },
      { field: 'vehicle_type', label: t('general.vehicleType', 'Vehicle Type'), render: (it) => it.vehicle_type ?? '-' },
      { field: 'name', label: t('general.bodyName', 'Body Name') },
      { field: 'company', label: t('masterCompany.companyName', 'Company Name') }
    ],
    [t]
  );

  const handleChange = useCallback(
    ({ left = [], right = [] }) => {
      const leftObjs = (left || []).map((id) => {
        const found = leftItems.find((c) => String(c.id) === String(id)) || rightItems.find((c) => String(c.id) === String(id));
        return found ? { ...found } : { id: String(id), name: String(id) };
      });

      const rightObjs = (right || []).map((id) => {
        const found = rightItems.find((c) => String(c.id) === String(id)) || leftItems.find((c) => String(c.id) === String(id));
        return found ? { ...found } : { id: String(id), name: String(id) };
      });

      const sortByCompany = (a, b) => (a.company || '').localeCompare(b.company || '');
      setLeftItems(leftObjs.sort(sortByCompany));
      setRightItems(rightObjs.sort(sortByCompany));

      setDirty(true);
      if (typeof onDirtyChange === 'function') onDirtyChange(true);
    },
    [leftItems, rightItems, onDirtyChange]
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const newVehicleIds = (rightItems || []).map((c) => Number(c.id));
      const roleId = roleVehicleObj?.id || 0;
      const payload = {
        id: roleId,
        vehicle_id: newVehicleIds,
        role_name: roleName
      };
      if (roleVehicleObj && roleVehicleObj.id) {
        await updateVehicleForRole(roleVehicleObj.id, payload);
      } else {
        await createVehicleForRole(payload);
      }

      setDirty(false);
      if (typeof onDirtyChange === 'function') onDirtyChange(false);
      if (typeof onSaved === 'function') onSaved({ roleId: roleVehicleObj.id, assigned: newVehicleIds });

      return { success: true, assigned: newVehicleIds };
    } catch (err) {
      console.error('Failed saving role vehicles:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [rightItems, roleVehicleObj, roleName, onDirtyChange, onSaved]);

  useImperativeHandle(
    ref,
    () => ({
      save,
      isDirty: () => dirty,
      getAssignedIds: () => (rightItems || []).map((c) => String(c.id ?? c))
    }),
    [save, dirty, rightItems]
  );

  if (!roleVehicleObj) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1">{t('masterRole.dataVehicleNotAvailable', 'Data Role Vehicle is not available.')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {loading || saving ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TransferListTable
          leftItems={leftItems}
          rightItems={rightItems}
          onChange={handleChange}
          titleLeft={t('masterRole.availableVehicle', 'Available Vehicle')}
          titleRight={t('masterRole.chosenVehicle', 'Chosen Vehicle')}
          itemKey="id"
          itemLabel="license_plate"
          showSearch
          columns={columns}
        />
      )}
    </Box>
  );
});

export default VehicleForRoleDialog;
